import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  bonus_credits: number;
  price_inr: number;
}

export interface CreditBalance {
  balance: number;
  lifetime_purchased: number;
  lifetime_spent: number;
}

export const CREDIT_COSTS = {
  outfit_generation: 15,
  ootd_rating: 10,
  virtual_tryon: 50,
  agent_chat: 3,
  clothing_analysis: 8,
  packing_list: 20,
} as const;

export type CreditAction = keyof typeof CREDIT_COSTS;

export function useStyleCredits() {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [packages, setPackages] = useState<CreditPackage[]>([]);

  const fetchBalance = useCallback(async () => {
    if (!user) {
      setBalance(0);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: { operation: 'get_balance' }
      });

      if (error) throw error;
      setBalance(data?.balance || 0);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const fetchPackages = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      setPackages(data || []);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    fetchPackages();
  }, [fetchBalance, fetchPackages]);

  const checkCredits = useCallback((action: CreditAction): boolean => {
    const cost = CREDIT_COSTS[action];
    return balance >= cost;
  }, [balance]);

  const spendCredits = useCallback(async (
    action: CreditAction,
    metadata?: Record<string, unknown>
  ): Promise<{ success: boolean; newBalance?: number; error?: string }> => {
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const cost = CREDIT_COSTS[action];
    if (balance < cost) {
      return { 
        success: false, 
        error: 'insufficient_credits',
      };
    }

    try {
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: { 
          operation: 'check_and_spend', 
          action,
          metadata
        }
      });

      if (error) throw error;

      if (data?.success) {
        setBalance(data.balance);
        return { success: true, newBalance: data.balance };
      }

      if (data?.error === 'insufficient_credits') {
        setBalance(data.available);
        return { 
          success: false, 
          error: 'insufficient_credits',
        };
      }

      return { success: false, error: data?.error || 'Unknown error' };
    } catch (err) {
      console.error('Failed to spend credits:', err);
      return { success: false, error: 'Service error' };
    }
  }, [user, balance]);

  const purchaseCredits = useCallback(async (packageId: string): Promise<boolean> => {
    if (!user) {
      toast.error('Please sign in to purchase credits');
      return false;
    }

    try {
      // Create order
      const { data: orderData, error: orderError } = await supabase.functions.invoke('create-credit-order', {
        body: { package_id: packageId }
      });

      if (orderError || !orderData?.order_id) {
        throw new Error(orderData?.error || 'Failed to create order');
      }

      // Load Razorpay if not loaded
      if (typeof window !== 'undefined' && !(window as any).Razorpay) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Razorpay'));
          document.body.appendChild(script);
        });
      }

      return new Promise((resolve) => {
        const options = {
          key: orderData.key_id,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'AURA',
          description: `${orderData.package.name} - ${orderData.package.total_credits} Style Credits`,
          image: `${window.location.origin}/aura-logo.png`,
          order_id: orderData.order_id,
          handler: async (response: any) => {
            try {
              const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-credit-purchase', {
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  package_id: packageId
                }
              });

              if (verifyError || !verifyData?.success) {
                throw new Error(verifyData?.error || 'Verification failed');
              }

              setBalance(verifyData.new_balance);
              toast.success(`Added ${verifyData.credits_added} Style Credits!`);
              resolve(true);
            } catch (err) {
              console.error('Payment verification failed:', err);
              toast.error('Payment verification failed. Please contact support.');
              resolve(false);
            }
          },
          modal: {
            ondismiss: () => resolve(false)
          },
          prefill: {
            email: user.email
          },
          theme: {
            color: '#D4AF37',
            backdrop_color: 'rgba(0, 0, 0, 0.8)',
          }
        };

        const razorpay = new (window as any).Razorpay(options);
        razorpay.open();
      });

    } catch (err) {
      console.error('Purchase failed:', err);
      toast.error('Failed to process purchase');
      return false;
    }
  }, [user]);

  const getCost = useCallback((action: CreditAction): number => {
    return CREDIT_COSTS[action];
  }, []);

  return {
    balance,
    isLoading,
    packages,
    checkCredits,
    spendCredits,
    purchaseCredits,
    refreshBalance: fetchBalance,
    getCost,
    CREDIT_COSTS
  };
}
